'use client'

import dynamic from 'next/dynamic'
import Image from 'next/image'
import Link from 'next/link'
import { ReactElement } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle'

import { INFURA_WEBSITE_URL } from '@infura/packages.common/constants'
import { Text } from '@infura/packages.ui/Text'

import { MetaMaskDeveloperFox, MetaMaskLogo } from 'components/MetaMaskLogo'

const Orbit = dynamic(() => import('./animations/orbiting-logos/Orbit'), { ssr: false })
interface AuthLayoutProps {
  children: React.ReactNode
  title?: string
  subtitle?: string | React.ReactNode
  customImage?: ReactElement<any, any>
}

const MetamaskLogoLink = (
  <Link href='/'>
    <span className='hidden md:inline'>
      <MetaMaskLogo />
    </span>
    <span className='md:hidden'>
      <MetaMaskDeveloperFox />
    </span>
  </Link>
)

const InfuraLogoLink = (
  <Link href={INFURA_WEBSITE_URL}>
    <Image alt='infura logo' height={20} src={'/images/logos/nav-logo.svg'} width={140} />
  </Link>
)

const AdvantageItem = ({ children }) => (
  <li className='mb-6'>
    <Text className='flex items-baseline'>
      <FontAwesomeIcon className='text-primary-default mr-2' icon={faCheckCircle} />
      {children}
    </Text>
  </li>
)

export const GreatTeamsCopy = {
  subtitle: (
    <>
      <div className='mb-8'>Built to scale from an idea to enterprise applications</div>
      <ul>
        <AdvantageItem>Battle-tested with a large community</AdvantageItem>
        <AdvantageItem>Integration is easy and intuitive</AdvantageItem>
        <AdvantageItem>Highest API availability</AdvantageItem>
      </ul>
    </>
  ),
  title: 'Great Web3 teams use MetaMask Developer'
}

export function AuthLayout({ children, customImage, subtitle, title }: Readonly<AuthLayoutProps>) {
  const InfuraDescClasses = 'lg:!w-[100px] xxl:!w-[120px] w-[80px] [&>a>svg]:w-[100%]'

  return (
    <div className='lg:flex lg:min-h-screen'>
      <div className='bg-background-default text-text-default relative hidden min-h-screen w-1/3 flex-col overflow-hidden lg:flex'>
        <div className='absolute -bottom-52 w-full'>
          <div className='relative m-auto'>
            <div className='relative m-auto'>{customImage || <Orbit />}</div>
          </div>
        </div>
        <div
          className='absolute w-full bg-gradient-to-t from-transparent via-gray-900 to-gray-900'
          style={{
            height: 800
          }}
        ></div>
        <div className='xxl:p-16 relative p-10 pb-0'>
          {MetamaskLogoLink}
          <h1 className='xxl:text-4xl xxl:leading-10 mt-10 text-2xl font-bold'>
            {title || 'Welcome back! Let’s get building.'}
          </h1>
          <div className='xxl:text-xl text-text-alternative pt-6'>
            {subtitle || 'Get the high availability blockchain APIs and developer tools you need to succeed.'}
          </div>
          <div className='flex items-center py-10'>
            <div className='xxl:!text-base py-1 pb-2 pr-3 text-xs lg:text-sm'>Previously known as</div>
            <div className={InfuraDescClasses}>{InfuraLogoLink}</div>
          </div>
        </div>
      </div>
      <div className='bg-black px-8 py-4 text-left lg:hidden'>{MetamaskLogoLink}</div>
      <div className='lg:flex lg:w-2/3 lg:flex-1 lg:flex-col lg:items-center'>{children}</div>
    </div>
  )
}
